import React, { memo, useEffect, useState } from "react";
import {
  TextField,
  InputAdornment,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { setAddCost, setCost } from "store/slices/orderCost";
import { useAppSelector } from "store/hooks/useAppSelector";
import {
  useAddToAddCostMutation,
  useGetAddCostQuery,
  useUpdateAddCostMutation,
} from "store/api";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import styles from "./PriceSection.styled";
import { orderStatuses } from "../CreatedOrder/constants";

const PriceSection = () => {
  const cost = useAppSelector((state) => state.orderCost.costDetails);
  const orderDetails = useAppSelector((state) => state.orderDetails);
  const addCostIncrementValue = useAppSelector(
    (state) => state.settings.addCostIncrementValue,
  );
  const [value, setValue] = useState<number>(cost.add_cost);
  const [updateAddCost] = useUpdateAddCostMutation();
  const [addToAddCost] = useAddToAddCostMutation();
  const dispatch = useAppDispatch();
  const { refetch } = useGetAddCostQuery(orderDetails.id || "", {
    skip: !orderDetails.id,
  });

  useEffect(() => {
    if (cost.add_cost !== value) {
      setValue(cost.add_cost);
    }
  }, [cost.add_cost]);

  useEffect(() => {
    dispatch(setAddCost(value));
  }, [value]);

  const handleIncrement = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!orderDetails.id) {
      setValue((prevValue) => prevValue + addCostIncrementValue);
      dispatch(setCost(+cost.order_cost + addCostIncrementValue));
    } else {
      try {
        await addToAddCost({
          id: orderDetails.id,
          amount: addCostIncrementValue,
        }).unwrap();
        setValue((prevValue) => prevValue + addCostIncrementValue);
        dispatch(setCost(+cost.order_cost + addCostIncrementValue));
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: error.data.Message,
        });
      }
    }
  };

  const handleDecrement = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!orderDetails.id) {
      setValue((prevValue) => Math.max(0, prevValue - addCostIncrementValue));
      dispatch(setCost(Math.max(0, +cost.order_cost - addCostIncrementValue)));
    } else {
      const { data: updatedAddCostValue } = await refetch();
      if (value && updatedAddCostValue) {
        try {
          await updateAddCost({
            id: orderDetails.id,
            amount: updatedAddCostValue.Amount - addCostIncrementValue,
          }).unwrap();
          setValue((prevValue) =>
            Math.max(0, prevValue - addCostIncrementValue),
          );
          dispatch(setCost(+cost.order_cost - addCostIncrementValue));
        } catch (error) {
          useNotificationToast({
            type: "error",
            message: error.data.Message,
          });
        }
      }
    }
  };

  const isAddCostShown =
    !orderDetails.id ||
    (orderDetails.id &&
      (orderDetails.status === orderStatuses[0].value ||
        orderDetails.status === orderStatuses[1].value));

  return (
    <Box sx={styles.priceContentWrap(!!orderDetails.id)}>
      <Box sx={styles.priceWrap}>
        {cost.order_cost && (
          <>
            <Typography sx={styles.price}>{cost.order_cost}</Typography>
            <Typography sx={styles.currency}>₴</Typography>
          </>
        )}
      </Box>
      <TextField
        type="number"
        sx={styles.addCost(!!isAddCostShown)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                onClick={handleDecrement}
                disabled={!cost.order_cost || !value}
              >
                <RemoveIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleIncrement} disabled={!cost.order_cost}>
                <AddIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default memo(PriceSection);
